import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Trophy, Heart, Clock, Home, Volume2, VolumeX, ArrowLeft, User, Gift, Loader2 } from 'lucide-react';
import { useGame } from '../../context/GameContext';
import { useRewards } from '../../context/RewardsContext';
import { PointsButton } from '../../components/RewardsWidgets';
import { getQuizQuestions } from '../../utils/mockData';
import soundEffects from '../../utils/soundEffects';
import CelebrationAnimation from '../../components/CelebrationAnimation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizGame = () => {
  const navigate = useNavigate();
  const { selectedAge, selectedSubject, addScore, saveProgress, gameProgress, setGameProgress } = useGame();
  const { awardCorrectAnswer, awardLessonComplete } = useRewards();
  const [allRounds, setAllRounds] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showRoundComplete, setShowRoundComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);

  // Map age groups to API format
  const getAgeGroupForAPI = (age) => {
    const ageMap = {
      'preschool': 'preschool',
      '3-5': 'preschool',
      'elementary': 'elementary',
      '6-8': 'elementary',
      'advanced': 'advanced',
      '9-12': 'advanced'
    };
    return ageMap[age] || 'elementary';
  };

  // Get the best available voice
  const getBestVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return null;
    
    const preferredVoices = [
      'Samantha', 'Google US English', 'Google UK English Female',
      'Microsoft Zira', 'Microsoft David', 'Karen', 'Moira', 'Fiona',
      'Victoria', 'Alex', 'Daniel', 'Tessa',
    ];
    
    for (const preferred of preferredVoices) {
      const voice = voices.find(v => v.name.includes(preferred));
      if (voice) return voice;
    }
    
    const englishVoice = voices.find(v => 
      v.lang.startsWith('en') && 
      (v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Natural') || !v.name.includes('eSpeak'))
    );
    if (englishVoice) return englishVoice;
    
    return voices.find(v => v.lang.startsWith('en')) || voices[0];
  }, []);

  // Load voices when available
  useEffect(() => {
    const loadVoices = () => {
      const voice = getBestVoice();
      if (voice) setSelectedVoice(voice);
    };
    
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [getBestVoice]);

  // Read aloud function with better voice
  const speakText = useCallback((text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  }, [selectedVoice]);

  // Cancel speech on unmount
  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  // Save progress when navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (questions.length > 0 && currentQuestion > 0) {
        saveProgress({
          currentQuestion,
          currentRound,
          score: totalScore,
          lives,
          correctAnswers: totalCorrect
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save progress when component unmounts (navigating away)
      if (questions.length > 0 && currentQuestion > 0) {
        saveProgress({
          currentQuestion,
          currentRound,
          score: totalScore,
          lives,
          correctAnswers: totalCorrect
        });
      }
    };
  }, [currentQuestion, currentRound, totalScore, lives, totalCorrect, questions.length, saveProgress]);

  // Restore progress if available
  useEffect(() => {
    if (gameProgress && questions.length > 0) {
      setCurrentQuestion(gameProgress.currentQuestion || 0);
      setCurrentRound(gameProgress.currentRound || 0);
      setTotalScore(gameProgress.score || 0);
      setLives(gameProgress.lives || 3);
      setTotalCorrect(gameProgress.correctAnswers || 0);
      // Clear gameProgress after restoring
      setGameProgress(null);
    }
  }, [gameProgress, questions.length, setGameProgress]);

  // Load quiz rounds on mount - try API first, fallback to mockData
  useEffect(() => {
    const loadQuizContent = async () => {
      setLoading(true);
      try {
        const ageGroup = getAgeGroupForAPI(selectedAge);
        const response = await axios.get(`${API}/content/quiz/${ageGroup}/${selectedSubject}`);
        
        if (response.data.rounds && response.data.rounds.length > 0) {
          // API data available - use it
          const rounds = response.data.rounds.map(r => r.questions);
          setAllRounds(rounds);
          setQuestions(rounds[0]);
          console.log(`Loaded ${rounds.length} rounds from API for ${ageGroup}/${selectedSubject}`);
        } else {
          // No API data - fallback to mockData
          console.log('No API data, falling back to mockData');
          loadFromMockData();
        }
      } catch (error) {
        console.log('API error, falling back to mockData:', error.message);
        loadFromMockData();
      }
      setLoading(false);
    };

    const loadFromMockData = () => {
      const quizData = getQuizQuestions(selectedAge, selectedSubject);
      if (Array.isArray(quizData) && Array.isArray(quizData[0]) && quizData[0][0]?.question) {
        setAllRounds(quizData);
        setQuestions(quizData[0]);
      } else {
        setAllRounds([quizData]);
        setQuestions(quizData);
      }
    };

    loadQuizContent();
  }, [selectedAge, selectedSubject]);

  useEffect(() => {
    if (timeLeft > 0 && !showFeedback && !showRoundComplete) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !showFeedback && !showRoundComplete) {
      handleWrongAnswer();
    }
  }, [timeLeft, showFeedback, showRoundComplete]);

  const handleAnswer = (answerIndex) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answerIndex);
    setShowFeedback(true);

    if (answerIndex === questions[currentQuestion].correctAnswer) {
      const points = 10 + timeLeft;
      setScore(score + points);
      setTotalScore(totalScore + points);
      addScore(points);
      setCorrectAnswers(correctAnswers + 1);
      setTotalCorrect(totalCorrect + 1);
      // Award +2 points for correct answer
      awardCorrectAnswer();
      // Play correct sound
      if (soundEnabled) soundEffects.playCorrect();
    } else {
      handleWrongAnswer();
      // Play incorrect sound
      if (soundEnabled) soundEffects.playIncorrect();
    }
  };

  const handleWrongAnswer = () => {
    setLives(lives - 1);
    if (lives - 1 <= 0) {
      setTimeout(() => {
        navigate('/result', {
          state: {
            score: totalScore,
            totalQuestions: allRounds.flat().length,
            correctAnswers: totalCorrect
          }
        });
      }, 1500);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setTimeLeft(30);
    } else {
      // Round complete - award lesson completion (+10 points)
      awardLessonComplete('quiz');
      if (currentRound < allRounds.length - 1) {
        // More rounds available
        setShowRoundComplete(true);
      } else {
        // All rounds complete - show celebration!
        setShowCelebration(true);
        if (soundEnabled) soundEffects.playCelebration();
      }
    }
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    navigate('/result', {
      state: {
        score: totalScore,
        totalQuestions: allRounds.flat().length,
        correctAnswers: totalCorrect,
        gameType: 'quiz'
      }
    });
  };

  const handleNextRound = () => {
    const nextRoundIndex = currentRound + 1;
    setCurrentRound(nextRoundIndex);
    setQuestions(allRounds[nextRoundIndex]);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setShowRoundComplete(false);
    setTimeLeft(30);
    setScore(0);
    setCorrectAnswers(0);
    setLives(3); // Reset lives for new round
  };

  if (loading || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card className="p-12 bg-white/95 shadow-2xl text-center" style={{ borderRadius: '32px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-purple-500 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-800">Loading Quiz...</h2>
        </Card>
      </div>
    );
  }

  // Round complete modal
  if (showRoundComplete) {
    const percentage = Math.round((correctAnswers / questions.length) * 100);
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
      }}>
        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl text-center max-w-md" style={{ borderRadius: '28px' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Quiz {currentRound + 1} Complete!</h2>
          <p className="text-xl text-gray-600 mb-2">
            Quiz {currentRound + 1} of {allRounds.length} finished!
          </p>
          <div className="my-6 p-4 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl">
            <p className="text-lg text-gray-700">Score: <span className="font-bold text-2xl text-orange-600">{score}</span></p>
            <p className="text-lg text-gray-700">Correct: <span className="font-bold">{correctAnswers}/{questions.length}</span> ({percentage}%)</p>
          </div>
          <Button
            onClick={handleNextRound}
            className="w-full py-6 text-xl font-bold text-white"
            style={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              borderRadius: '16px'
            }}
          >
            Next Quiz →
          </Button>
        </Card>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    }}>
      {/* Celebration Animation */}
      <CelebrationAnimation 
        show={showCelebration} 
        onComplete={handleCelebrationComplete}
        message="Quiz Complete!"
        duration={3500}
      />

      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/game-format')}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-end">
            <PointsButton />
            <Button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <User className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => navigate('/rewards')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <Gift className="w-4 h-4" />
            </Button>
            <Card className="px-3 py-2 bg-white/95" style={{ borderRadius: '12px' }}>
              <span className="font-bold text-sm md:text-lg">Quiz {currentRound + 1}/{allRounds.length}</span>
            </Card>
            <Card className="px-3 py-2 bg-white/95 flex items-center gap-2" style={{ borderRadius: '12px' }}>
              <Trophy className="w-5 h-5 text-yellow-600" />
              <span className="font-bold text-lg">{totalScore}</span>
            </Card>
            <Card className="px-3 py-2 bg-white/95 flex items-center gap-2" style={{ borderRadius: '12px' }}>
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="font-bold text-lg">{timeLeft}s</span>
            </Card>
            <Card className="px-3 py-2 bg-white/95 flex items-center gap-2" style={{ borderRadius: '12px' }}>
              {[...Array(3)].map((_, i) => (
                <Heart
                  key={i}
                  className={`w-5 h-5 ${i < lives ? 'fill-red-500 text-red-500' : 'text-gray-300'}`}
                />
              ))}
            </Card>
          </div>
        </div>

        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl" style={{ borderRadius: '28px' }}>
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600 font-semibold">Question {currentQuestion + 1} of {questions.length}</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          <div className="flex items-center justify-center gap-3 mb-8">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 text-center">
              {currentQ.question}
            </h2>
            <button
              onClick={() => speakText(currentQ.question)}
              className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                isSpeaking 
                  ? 'bg-pink-500 text-white animate-pulse' 
                  : 'bg-pink-100 text-pink-600 hover:bg-pink-200'
              }`}
              title="Read question aloud"
            >
              <Volume2 className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentQ.options.map((option, index) => (
              <Button
                key={index}
                onClick={() => handleAnswer(index)}
                disabled={showFeedback}
                className={`p-6 text-xl font-semibold transition-all duration-300 ${
                  showFeedback
                    ? index === currentQ.correctAnswer
                      ? 'bg-green-500 text-white scale-105'
                      : selectedAnswer === index
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:scale-105'
                }`}
                style={{ borderRadius: '16px' }}
              >
                {option}
              </Button>
            ))}
          </div>

          {showFeedback && (
            <div className="mt-6 text-center">
              <p className={`text-2xl font-bold ${selectedAnswer === currentQ.correctAnswer ? 'text-green-600' : 'text-red-600'}`}>
                {selectedAnswer === currentQ.correctAnswer ? '🎉 Correct!' : '😔 Wrong!'}
              </p>
              <Button
                onClick={nextQuestion}
                className="mt-4 px-8 py-4 text-lg font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:scale-105"
                style={{ borderRadius: '12px' }}
              >
                {currentQuestion + 1 < questions.length ? 'Next Question →' : (currentRound < allRounds.length - 1 ? 'Finish Quiz →' : 'See Results →')}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default QuizGame;
